/**
 * m3 echo Frontend - Billing Modules Tab
 *
 * Table of all billing modules with CRUD actions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Typography,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { BillingModule, billingApi } from '../../services/billing';
import ModuleDialog from './ModuleDialog';

const typeColors: Record<string, 'primary' | 'secondary' | 'success' | 'info'> = {
  quota: 'primary',
  model: 'secondary',
  feature: 'success',
  custom: 'info',
};

const typeLabels: Record<string, string> = {
  quota: 'Kontingent',
  model: 'KI-Modell',
  feature: 'Feature',
  custom: 'Custom',
};

const ModulesTab: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [modules, setModules] = useState<BillingModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editModule, setEditModule] = useState<BillingModule | null>(null);

  const loadModules = useCallback(async () => {
    try {
      setLoading(true);
      const response = await billingApi.getModules();
      setModules(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Module');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadModules();
  }, [loadModules]);

  const handleEdit = (module: BillingModule) => {
    setEditModule(module);
    setDialogOpen(true);
  };

  const handleDelete = async (module: BillingModule) => {
    if (!window.confirm(`Modul "${module.display_name}" wirklich löschen?`)) return;
    try {
      await billingApi.deleteModule(module.id);
      loadModules();
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen');
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditModule(null);
  };

  const handleDialogSaved = () => {
    handleDialogClose();
    loadModules();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
          {modules.length} Modul{modules.length !== 1 ? 'e' : ''}
        </Typography>
      </Box>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Typ</TableCell>
              {!isMobile && <TableCell>Preis (EUR)</TableCell>}
              {!isMobile && <TableCell>Kulanzzeit</TableCell>}
              <TableCell>Status</TableCell>
              {!isMobile && <TableCell>Zuweisungen</TableCell>}
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {modules.map((module) => (
              <TableRow key={module.id}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {module.display_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {module.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={typeLabels[module.module_type] || module.module_type}
                    color={typeColors[module.module_type] || 'default'}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                {!isMobile && (
                  <TableCell>
                    {Number(module.base_price).toFixed(2)} €
                  </TableCell>
                )}
                {!isMobile && (
                  <TableCell>
                    {module.grace_period_days > 0 ? `${module.grace_period_days} Tage` : '-'}
                  </TableCell>
                )}
                <TableCell>
                  <Chip
                    label={module.is_active ? 'Aktiv' : 'Inaktiv'}
                    color={module.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                {!isMobile && <TableCell>{module.assignment_count}</TableCell>}
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleEdit(module)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <Tooltip
                    title={
                      module.assignment_count > 0
                        ? `${module.assignment_count} Zuweisung(en) vorhanden - zuerst entfernen`
                        : 'Löschen'
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(module)}
                        disabled={module.assignment_count > 0}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {modules.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Keine Module vorhanden
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ModuleDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSaved={handleDialogSaved}
        module={editModule}
      />
    </Box>
  );
};

export default ModulesTab;
